import { useOutletContext } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useMutation, useQueryClient } from "react-query";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

import ImageContainer from "../../../components/imageContainer";
import CountDown from "../../../components/countdown";
import useUser from "../../../hooks/useUser";
import useSnackbar from "../../../components/snackbar/hooks/useSnackbar";
import SvgIcon from "../../../components/svgIcon";
import { updateDesign } from "../../../apis/designsApi";
import {
  DesignState,
  markAsUnchanged,
  setComposition,
  setImage,
  setLastDisplayedImage,
} from "../../../features/designSlice";
import { getErrorMessage } from "../../../utils/errors";
import { CART_ITEMS_QUERY_KEY } from "../../../queries-keys/cartItems";
import { SESSION_QUERY_KEY } from "../../../queries-keys/sessions";
import { SAVE_ICON_URL } from "../../../constants/iconsUrls";
import { DEFAULT_DESIGN_IMAGE } from "../../../constants/imagesUrls";
import { DesignOutletContext } from "..";
import Design from "../../../models/Design";
import useGetDesignPreview from "../../../hooks/useGetDesignPreview";
import useStepper from "../../../components/stepper/hooks/useStepper";
import { DESIGN_CATEGORY } from "../../../constants/designs";
import { getShapesFromComposition } from "../../../utils/designs";
import { useEffect } from "react";

const FRONT_URL = process.env.REACT_APP_FRONTEND_URL;

const TwoDViewer = ({}) => {
  const { sessionRemainingTime } =
    useOutletContext<DesignOutletContext>() || {};

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { user } = useUser();
  const { handleOpenSnackbar } = useSnackbar();
  const { currentStepData } = useStepper();

  const {
    id: designId,
    isChanged: isDesignChanged,
    image,
    imageUrl,
    lastDisplayedImage,
    isLastChangeMaterial,
    layout_body: selectedLayoutBody,
    layout_size: selectedLayoutSize,
    shapes: selectedShapes,
    materials: selectedMaterials,
    price,
    composition,
  } = useSelector((state: { design: DesignState }) => state.design);

  const isLayout = currentStepData?.category === DESIGN_CATEGORY.LAYOUT;
  const isMaterial = currentStepData?.category === DESIGN_CATEGORY.MATERIAL;

  const {
    designComposition: newDesignComposition,
    image: newDesignImage,
    imageUrl: newDesignImageUrl,
    shapes: newSelectedShapes,
    materials: newSelectedMaterials,
    isGeneratingPreview,
  } = useGetDesignPreview({
    layout: (selectedLayoutSize?.code || "") + (selectedLayoutBody?.code || ""),
    design_composition: composition || undefined,
    step_info:
      !currentStepData?.category || isLayout
        ? undefined
        : {
            step_type: isMaterial ? "MT" : "SH",
            number_of_shapes:
              getShapesFromComposition(composition).length || undefined,
            shape_number: currentStepData?.elementOrder,
            material_number: isMaterial
              ? currentStepData?.materialOrder
              : undefined,
          },
    enabled: Boolean(selectedLayoutBody?.id && selectedLayoutSize?.id),
  });

  const { mutate: updateDesignMutation, isLoading: isSavingDesign } =
    useMutation(
      (updatedData: Partial<Design>) =>
        updateDesign(designId || 0, updatedData),
      {
        onError: (error) => {
          handleOpenSnackbar(getErrorMessage(error), "error");
        },
        onSuccess: () => {
          dispatch(markAsUnchanged());
          queryClient.invalidateQueries(CART_ITEMS_QUERY_KEY(user?.id));
        },
      }
    );

  const handleDesignSaving = () => {
    if (!isDesignChanged) {
      return;
    }

    updateDesignMutation({
      layout_body: selectedLayoutBody,
      layout_size: selectedLayoutSize,
      composition,
      shapes: selectedShapes,
      materials: selectedMaterials,
      image,
      price,
    });
  };

  useEffect(() => {
    if (newDesignImage) {
      dispatch(setImage({ image: newDesignImage, imageUrl: newDesignImageUrl }));
    }

    if (newDesignComposition) {
      dispatch(
        setComposition({
          composition: newDesignComposition,
          shapes: newSelectedShapes,
          materials: newSelectedMaterials,
        })
      );
    }
  }, [
    newDesignComposition,
    newDesignImage,
    newDesignImageUrl,
    newSelectedShapes,
    newSelectedMaterials,
    dispatch,
  ]);

  return (
    <div className="relative aspect-video overflow-hidden rounded-lg border">
      <div className="absolute left-3 top-2 z-[1] lg:top-3">
        <CountDown
          time={sessionRemainingTime || 0}
          onExpire={() => queryClient.invalidateQueries(SESSION_QUERY_KEY())}
          beforeExpire={handleDesignSaving}
        />
      </div>
      <div className="absolute right-3 top-2 z-[1] lg:top-3">
        {isSavingDesign ? (
          <AiOutlineLoading3Quarters className="shrink-0 animate-spin text-[20px] text-primary lg:text-[30px]" />
        ) : (
          <SvgIcon
            src={SAVE_ICON_URL}
            className={`shrink-0 h-[30px] lg:h-[40px] ${
              isDesignChanged
                ? "cursor-pointer !text-highlight hover:!text-red-700"
                : "text-gray-300"
            }`}
            onClick={handleDesignSaving}
          />
        )}
      </div>
      {isGeneratingPreview ? (
        <div className="absolute left-1/2 top-1/2 z-[1] flex -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-lg bg-white/70">
          <AiOutlineLoading3Quarters className="animate-spin text-[40px] text-primary lg:text-[60px]" />
        </div>
      ) : (
        <ImageContainer
          imageMagnifier
          containerClassName={"h-full w-full"}
          imgClassName={"w-full h-full object-cover"}
          src={
            imageUrl
              ? imageUrl
              : isLastChangeMaterial && lastDisplayedImage
              ? lastDisplayedImage
              : DEFAULT_DESIGN_IMAGE
          }
          onLoad={({ currentTarget }: { currentTarget: HTMLImageElement }) => {
            if (currentTarget.src !== FRONT_URL + DEFAULT_DESIGN_IMAGE) {
              dispatch(setLastDisplayedImage(currentTarget.src));
            }
          }}
          onError={({ currentTarget }: { currentTarget: HTMLImageElement }) => {
            currentTarget.onerror = null;
            currentTarget.src =
              isLastChangeMaterial && lastDisplayedImage
                ? lastDisplayedImage
                : DEFAULT_DESIGN_IMAGE;
          }}
          alt="Design Image"
        />
      )}
    </div>
  );
};

export default TwoDViewer;
